import { useState } from 'react';

import {
  FieldValidationRule,
  required as requiredRule,
  validateField as validateWithRules,
} from '../pages/application/utils/fieldValidation';

export interface ValidationRule<T> {
  field: keyof T;
  required?: boolean;
  label: string;
  validate?: (value: unknown, formData: T) => string | undefined;
  validationRules?: FieldValidationRule[];
}

export interface UseBlurValidationOptions<T> {
  rules: ValidationRule<T>[];
  formData: T;
  externalErrors?: Partial<Record<keyof T, string>>;
}

export function useBlurValidation<T>({ rules, formData, externalErrors = {} }: UseBlurValidationOptions<T>) {
  const [touchedFields, setTouchedFields] = useState<Set<keyof T>>(new Set());
  const [localErrors, setLocalErrors] = useState<Partial<Record<keyof T, string>>>({});

  /**
   * ACP-2077 (Section 508): the shared required rule says "This field is required",
   * which names nothing once read away from the field — in an error list, or by a
   * screen reader browsing errors. Every rule here carries the field's visible label,
   * so name it: "Visa Number is required". Applies to the workflow's errors too.
   */
  const nameTheField = (field: keyof T, message: string | undefined): string | undefined => {
    if (message !== requiredRule.errorMessage) return message;
    const label = rules.find((r) => r.field === field)?.label;

    if (!label) return message;

    // A question reads badly as "…the United States? is required".
    return label.trim().endsWith('?') ? 'An answer to this question is required' : `${label} is required`;
  };

  const validateField = (field: keyof T, value: unknown): string | undefined => {
    const rule = rules.find((r) => r.field === field);
    if (!rule) return undefined;

    // Use validationRules if provided (from fieldValidation.ts)
    if (rule.validationRules && rule.validationRules.length > 0) {
      const result = validateWithRules(value, rule.validationRules, formData as Record<string, unknown>);
      if (!result.isValid) {
        return nameTheField(field, result.errorMessage);
      }

      return undefined;
    }

    // Custom validation
    if (rule.validate) {
      const error = rule.validate(value, formData);
      if (error) return error;
    }

    // Required validation
    if (rule.required) {
      if (value === undefined || value === null || value === '' || (typeof value === 'string' && value.trim() === '')) {
        return `${rule.label} is required`;
      }
    }

    return undefined;
  };

  const handleBlur = (field: keyof T) => {
    // Mark field as touched
    setTouchedFields((prev) => new Set(prev).add(field));

    // Validate the field
    const value = formData[field];
    const error = validateField(field, value);

    // Update local errors
    setLocalErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const handleChange = (field: keyof T, value: unknown) => {
    // Clear local error when user starts typing (if field was touched)
    if (touchedFields.has(field)) {
      const error = validateField(field, value);
      setLocalErrors((prev) => ({
        ...prev,
        [field]: error,
      }));
    }
  };

  const getFieldError = (field: keyof T): string | undefined => {
    return localErrors[field] || nameTheField(field, externalErrors[field]);
  };

  return {
    getFieldError,
    handleBlur,
    handleChange,
    localErrors,
    touchedFields,
  };
}
